import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOff, Loader2 } from 'lucide-react';
import { calibrated, loadMapImages } from '../../utils/mapImages';
import type { MapImage } from '../../utils/mapImages';
import { monsterName } from '../../utils/worldFormat';
import type { Spawn, WorldData } from '../../types/world';
type State = {
  status: 'loading';
} | {
  status: 'ok';
  img?: MapImage;
} | {
  status: 'error';
};
const GRID = 256;
const pct = (v: number) => `${v / GRID * 100}%`;

/** Imagen del mapa con los spots marcados. Las coordenadas escritas siguen siendo la referencia. */
export function MapImageView({
  w,
  mapId,
  spots
}: {
  w: WorldData;
  mapId: number;
  spots: Spawn[];
}) {
  const [state, setState] = useState<State>({
    status: 'loading'
  });
  const [selected, setSelected] = useState<string | undefined>();
  useEffect(() => {
    let alive = true;
    setState({
      status: 'loading'
    });
    loadMapImages().then(m => alive && setState({
      status: 'ok',
      img: m.get(mapId)
    })).catch(() => alive && setState({
      status: 'error'
    }));
    return () => {
      alive = false;
    };
  }, [mapId]);
  if (state.status === 'loading') {
    return <p className="flex items-center gap-2 text-sm text-gray-400" data-id="element-1442" data-mp="1x16zwr60dml7">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1443" data-mp="1x3jecuck5eti" /> Cargando la imagen del mapa…
      </p>;
  }
  if (state.status === 'error' || !state.img) {
    return <p className="flex items-start gap-2 text-sm text-gray-500" data-id="element-1444" data-mp="15asqp88xwyfv">
        <ImageOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1445" data-mp="10by19nggwbms" />
        {state.status === 'error' ? 'No se pudo cargar la imagen del mapa.' : 'Este mapa no tiene imagen en la exportación.'} Usa las coordenadas de la lista.
      </p>;
  }
  const img = state.img;
  const canMark = calibrated(img);
  // Eje horizontal / vertical según 47_map_images (p. ej. horizontal = Y, vertical = X).
  const axis = (s: Spawn, which: 'h' | 'v') => {
    const k = which === 'h' ? img.horizontal : img.vertical;
    return k === 'X' ? [s.xMin, s.xMax] : [s.yMin, s.yMax];
  };
  const marked = canMark ? spots.filter(s => [...axis(s, 'h'), ...axis(s, 'v')].every(v => v !== undefined)) : [];
  const sel = marked.find(s => s.id === selected);
  return <figure className="max-w-xl" data-id="element-1446" data-mp="1rcgsfzood90r">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black" data-id="element-1447" data-mp="1wdwdhrqurfj5">
        <img src={img.src} alt={`Mapa de ${w.maps.get(mapId)?.name ?? mapId}`} className="block h-auto w-full [image-rendering:pixelated]" data-id="element-1448" data-mp="znnfqbxhedvx" />
        {marked.map(s => {
        const [h1, h2] = axis(s, 'h') as number[];
        const [v1, v2] = axis(s, 'v') as number[];
        const point = h1 === h2 && v1 === v2;
        const active = s.id === selected;
        return <button key={s.id} type="button" aria-label={`${monsterName(w, s.monsterId)}: X ${s.xMin}${s.xMax !== s.xMin ? `–${s.xMax}` : ''} / Y ${s.yMin}${s.yMax !== s.yMin ? `–${s.yMax}` : ''}`} onClick={() => setSelected(active ? undefined : s.id)} className={`absolute border transition-colors duration-150 ${point ? 'rounded-full' : 'rounded-sm'} ${active ? 'z-10 border-white bg-[#D4AF37]/70' : 'border-[#D4AF37] bg-[#D4AF37]/25 hover:bg-[#D4AF37]/50'}`} style={point ? {
          left: pct(h1),
          top: pct(v1),
          width: 10,
          height: 10,
          transform: 'translate(-50%, -50%)'
        } : {
          left: pct(Math.min(h1, h2)),
          top: pct(Math.min(v1, v2)),
          width: pct(Math.abs(h2 - h1) + 1),
          height: pct(Math.abs(v2 - v1) + 1),
          minWidth: 6,
          minHeight: 6
        }} data-id="element-1449" data-mp="fz4ntml3cmdj" />;
      })}
      </div>
      <figcaption className="mt-2 text-xs text-gray-500" data-id="element-1450" data-mp="3bx3se246z3dx">
        {sel ? <span className="text-sm text-gray-200" data-id="element-1451" data-mp="37uh4udek04mj">
            <Link to={`/guia/mapas/spot/${encodeURIComponent(sel.id)}`} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-1452" data-mp="37u7bcfuavfc3">{monsterName(w, sel.monsterId)}</Link>{' '}
            · X {sel.xMin}{sel.xMax !== sel.xMin ? `–${sel.xMax}` : ''} / Y {sel.yMin}{sel.yMax !== sel.yMin ? `–${sel.yMax}` : ''}
          </span> : canMark ? <>Toca una marca para ver el spot. Eje horizontal = {img.horizontal}, eje vertical = {img.vertical}. Las áreas se dibujan completas; la posición sobre la imagen es orientativa y las coordenadas escritas son la referencia.</> : <>La imagen no declara su orientación o no es cuadrada: no se colocan marcas para no inventar posiciones.</>}
      </figcaption>
    </figure>;
}